import { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { usePredefinedActivityImages } from '@/hooks/useActivities';
import type { ActivityImageValue, PredefinedActivityImage } from '@/types/upload';

interface PredefinedImageSelectorProps {
  isActive: boolean;
  selectedImageId: number | null;
  onActivate: () => void;
  onSelect: (value: ActivityImageValue) => void;
  warning?: string | null;
}

export default function PredefinedImageSelector({
  isActive,
  selectedImageId,
  onActivate,
  onSelect,
  warning,
}: PredefinedImageSelectorProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const { data: predefinedImages } = usePredefinedActivityImages();

  const handleSelect = (image: PredefinedActivityImage) => {
    onSelect({
      type: 'predefined',
      predefinedActivityImageId: parseInt(image.id, 10),
      viewUrl: image.imageUrl || '',
    });
    setShowDropdown(false);
  };

  const selectedLabel = selectedImageId
    ? predefinedImages?.find((img) => parseInt(img.id, 10) === selectedImageId)?.label ?? 'Selected'
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onActivate}
          className="w-5 h-5 flex items-center justify-center cursor-pointer"
        >
          {isActive ? (
            <Check className="w-4 h-4 text-admin-accent-blue" />
          ) : (
            <span className="w-4 h-4 rounded-sm border border-admin-slate-300/30 hover:border-admin-slate-300/60 transition-colors" />
          )}
        </button>
        <span className="text-sm font-medium text-admin-slate-400">Predefined Image</span>
      </div>

      <div className="ml-7 relative">
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center justify-between px-3 py-2 rounded-lg border border-admin-slate-300/20 bg-admin-dark-700 text-admin-slate-400 hover:border-admin-slate-300/40 transition-colors text-sm"
        >
          <span>{selectedLabel || 'Select predefined image...'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
        </button>

        {showDropdown && predefinedImages && (
          <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-lg border border-admin-slate-300/20 bg-admin-dark-800 shadow-lg">
            {predefinedImages.map((img) => (
              <button
                key={img.id}
                type="button"
                onClick={() => handleSelect(img)}
                className="w-full flex items-center gap-3 px-4 py-2 hover:bg-admin-dark-700 transition-colors text-left"
              >
                {img.imageUrl && (
                  <img
                    src={img.imageUrl}
                    alt={img.label}
                    className="w-10 h-7 object-cover rounded"
                  />
                )}
                <span className="text-admin-slate-400 text-sm">{img.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {warning && !isActive && (
        <p className="text-xs text-amber-400 ml-7">{warning}</p>
      )}
    </div>
  );
}
